import type { ColumnDef } from '@tanstack/react-table';
import { BoolCell, DateCell } from '@/components/data-table/cells';
import { getTermsVersionNote, type TermsRow } from '@/services/api/terms';
import { formatDate } from '@/utils/format';

const VersionCell = ({ version }: { version: string }) => {
  const note = getTermsVersionNote(version);
  return (
    <span className={note ? 'underline decoration-dotted underline-offset-2' : undefined} title={note ?? undefined}>
      {version}
    </span>
  );
};
VersionCell.displayName = 'VersionCell';

/**
 * One column config reused by both tabs (legacy duplicated it).
 * Rows keep raw epoch/ISO date values so column sort is chronological instead
 * of lexical (deliberate fix); cells format at render.
 */
export const TERMS_COLUMNS: ColumnDef<TermsRow, unknown>[] = [
  {
    accessorKey: 'pid',
    header: 'PID',
    cell: ({ row }) => <span className="font-mono text-xs">{row.original.pid}</span>,
  },
  {
    accessorKey: 'created',
    header: 'Account Created',
    cell: ({ row }) => <DateCell value={row.original.created} />,
  },
  {
    accessorKey: 'early_adopter',
    header: 'Early Adopter',
    cell: ({ row }) => <BoolCell value={row.original.early_adopter} />,
  },
  {
    accessorKey: 'email',
    header: 'Email',
    cell: ({ row }) => row.original.email || <span className="italic text-muted-foreground">not set</span>,
  },
  {
    accessorKey: 'version',
    header: 'Terms Version',
    cell: ({ row }) => <VersionCell version={row.original.version} />,
  },
  {
    accessorKey: 'date',
    header: 'Terms Accepted',
    cell: ({ row }) => <DateCell value={row.original.date} />,
  },
];

/** CSV headers = the column TITLES, in column order */
export const TERMS_CSV_HEADERS = ['PID', 'Account Created', 'Early Adopter', 'Email', 'Terms Version', 'Terms Accepted'] as const;

/**
 * Legacy formatRowForCsv exported the ROW values: dates were already formatted
 * strings on legacy rows and booleans stayed raw true/false (not the cell
 * emoji). Rows here keep raw timestamps, so dates are formatted at
 * export time to preserve the legacy CSV content.
 */
export const formatTermsRowForCsv = (row: TermsRow): Record<string, unknown> => ({
  PID: row.pid,
  'Account Created': formatDate(row.created),
  'Early Adopter': row.early_adopter,
  Email: row.email,
  'Terms Version': row.version,
  'Terms Accepted': formatDate(row.date),
});
